import { useMemo, useState, useEffect, useContext } from "react";
import { getAllFirmNames } from "../../Services/Firm";
import { firmShortLabel } from "../../utils/firmDisplayName";
import { getOfficeJurisdictionOptions } from "../../Services/Office";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import {
  attendeesStatusEnum,
  attendeesTypeEnum,
  paymentMethodEnum,
  statusEnum,
} from "../../Enums/RegistrationEnum";
import { formatTimeSpanToString } from "../../utils/functions";
import { getEventLookupOptions, getEventById } from "../../Services/Event";
import { getFirmById } from "../../Services/Firm";
import { IEvent } from "../../interfaces/models/IEvent";
import IFirm from "../../interfaces/models/IFirm";
import SendMail from "../../components/Atomos/Icons/ViewMode/SendMail";
import Log from "../../components/Atomos/Icons/Log";
import { getRegistrationById } from "../../Services/Registration";
import { toastError } from "../../utils/toast";
import {
  getRegistrationDisplayName,
  sendDuePaymentReminderForRegistration,
} from "../../utils/sendDuePaymentReminder";
import PaymentReminderProgressModal, {
  type PaymentReminderIssue,
} from "./components/PaymentReminderProgressModal";
import PaymentReminderBatchLogModal from "./components/PaymentReminderBatchLogModal";
import {
  savePaymentReminderBatchLog,
  type IPaymentReminderLogRecipient,
} from "../../Services/PaymentReminderLog";
import AuthContext from "../../store/AuthContext";
import "../EventsPage/Events.css";
import { Link, useParams } from "react-router-dom";
import { useEventLoader } from "../../hooks/Events/useEventLoader";
import PencilEdit from "../../components/Atomos/Icons/PencilEdit";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import {
  CUSTOM_FILTER_CHIP_ID,
  IQuickFilter,
  IFilterData,
} from "../../components/Organismos/Table/FilteredTable.types";
import Button from "../../components/staff/Button";
import { useDispatch, useSelector } from "react-redux";
import { Appstore, ResetEvent } from "../../redux";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import TemplateDateFlightsPicker from "../SendEmail/TemplateDateFlightsPicker";
import { summarizeTemplateDateFilterSelection } from "../SendEmail/templateDateFilter";
import {
  buildEventDateFilter,
  buildRegistrationDateFilter,
} from "./attendeesRegistrationDateFilters";
import {
  REGISTRATION_ALL_ATTENDEES_QUICK_FILTER,
  REGISTRATION_WSG_STAFF_QUICK_FILTER,
} from "./registrationQuickFilters";
import { isMemberFirmRecipient } from "../../utils/wsgStaffFirms";
import ModalConfirmation from "../ManageFirm/GeneralTab/MainBox/ModalConfirmation";
import { getEventYearFilterOptions } from "../../utils/eventYearFilterOptions";
import {
  DEFAULT_TEMPLATE_DATE_FILTER,
  type TemplateDateFilterValue,
} from "../../utils/templateDateFilterTypes";

const { guest, ...attendeesTypeEnumWithoutGuest } = attendeesTypeEnum;
const paidStatusOptions = Object.entries(statusEnum)
  .filter(([id]) => id !== "unpaidSPK")
  .map(([id, name]) => ({
    id,
    name,
  }));

const UNPAIDS_PAGE_EXCLUDED_REGION_NAMES = new Set(
  ["Canada", "USA", "Latin America"].map((n) => n.trim().toLowerCase()),
);

const DEFAULT_UNPAID_PAID_SELECTIONS = [
  { id: "pending" as const, name: statusEnum.pending },
  { id: "partial" as const, name: statusEnum.partial },
];

const UNPAID_PAYMENT_STATUS_FILTER = {
  paymentStatus: {
    value: ["pending", "partial"],
    comparisonType: "EQUAL" as const,
  },
};

const DEFAULT_UNPAID_QUICK_FILTER_SEED = {
  inputFilters: {
    Paid: DEFAULT_UNPAID_PAID_SELECTIONS,
  },
  query: UNPAID_PAYMENT_STATUS_FILTER,
};

const REMINDER_ELIGIBLE_PAYMENT_STATUSES = new Set([
  "pending",
  "partial",
  "unpaidSPR",
  "unpaidSPK",
]);

function isReminderEligiblePaymentStatus (status: unknown): boolean {
  return (
    typeof status === "string" &&
    REMINDER_ELIGIBLE_PAYMENT_STATUSES.has(status)
  );
}

function buildAttendeesPersistentFilter (eventId?: string) {
  if (!eventId) {
    return {};
  }
  return {
    eventId: { value: eventId, comparisonType: "EQUAL" as const },
    ...UNPAID_PAYMENT_STATUS_FILTER,
  };
}

function templateDateFlightFilterChips (filterValue: unknown) {
  const label = summarizeTemplateDateFilterSelection(
    filterValue as TemplateDateFilterValue | null | undefined,
  );
  if (!label) {
    return [];
  }
  return [{ id: CUSTOM_FILTER_CHIP_ID, name: label }];
}

export default function Attendees () {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [eventsMap, setEventsMap] = useState<Map<string, string>>(new Map());
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [isReminderComplete, setIsReminderComplete] = useState(false);
  const [emailProgress, setEmailProgress] = useState({
    completed: 0,
    total: 0,
  });
  const [reminderResults, setReminderResults] = useState({
    sentCount: 0,
    skippedCount: 0,
    failedCount: 0,
  });
  const [reminderIssues, setReminderIssues] = useState<PaymentReminderIssue[]>(
    [],
  );
  const [reminderLogRefreshKey, setReminderLogRefreshKey] = useState(0);
  const [isReminderLogModalOpen, setIsReminderLogModalOpen] = useState(false);
  const [isReminderConfirmOpen, setIsReminderConfirmOpen] = useState(false);
  const { loggedPerson } = useContext(AuthContext);
  const [firmData, setFirmData] = useState<any[]>([]);
  const [jurisdictionData, setJurisdictionData] = useState<
    { id: string; name: string }[]
  >([]);
  const { regionData } = UseRegionList();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const dispatch = useDispatch();
  const { eventId: routeEventId } = useParams<{ eventId?: string }>();
  const [persistentFilter, setPersistentFilter] = useState<any>(() =>
    buildAttendeesPersistentFilter(routeEventId),
  );
  const attendeesTableDefaultPageSize = 200;
  const shouldLoadEventForRoute = Boolean(routeEventId);

  useEffect(() => {
    if (!routeEventId) {
      dispatch(ResetEvent());
    }
  }, [dispatch, routeEventId]);

  useEventLoader(routeEventId, shouldLoadEventForRoute);
  const event = useSelector((state: Appstore) => state.event) as IEvent;

  const { practiceIndustryData } = UsePracticeIndustryList();
  const endpoint = "/registration";

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const events = await getEventLookupOptions();
        const map = new Map<string, string>();

        events.forEach((event) => {
          if (event.id && event.title) {
            map.set(event.id, event.title);
          }
        });

        setEventsMap(map);
      } catch (error) {
        setEventsMap(new Map());
      }
    };
    loadEvents();
  }, []);

  useEffect(() => {
    let active = true;
    getAllFirmNames()
      .then((firms) => {
        if (active) {
          setFirmData(Array.isArray(firms) ? firms : []);
        }
      })
      .catch(() => {
        if (active) {
          setFirmData([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getOfficeJurisdictionOptions()
      .then((options) => {
        if (active) {
          setJurisdictionData(options);
        }
      })
      .catch(() => {
        if (active) {
          setJurisdictionData([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setPersistentFilter(buildAttendeesPersistentFilter(routeEventId));
  }, [routeEventId]);

  const columnsDef = useMemo(
    () => [
      {
        id: "actions",
        header: "Edit",
        enableSorting: false,
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
          const registrationId = row.id;
          return (
            <Link
              to={`/events-staff/${row.eventId}/attendees/Edit/${registrationId}`}
              className="flex flex-row items-center cursor-pointer hover:opacity-80"
            >
              <div className="w-5 h-5 [&>div>svg]:w-full [&>div>svg]:h-full [&>div>svg>path]:fill-current">
                <PencilEdit />
              </div>
            </Link>
          );
        },
      },
      { id: "personName", header: "Name", width: 140 },
      {
        id: "eventName",
        header: "Event",
        width: 140,
        cell: ({ row }: { row: any }) => {
          const title = row.eventName ?? "-";
          const eid = row.eventId;
          if (!eid) {
            return <span>{title}</span>;
          }
          return (
            <Link
              to={`/events-staff/${eid}/attendees`}
              className="hover:underline"
            >
              {title}
            </Link>
          );
        },
      },
      { id: "attendeeType", header: "Reg Type" },
      { id: "registrationDate", header: "Reg Date" },
      {
        id: "eventDate",
        header: "Event Date",
        width: 120,
      },
      { id: "firmName", header: "Firm", width: 140 },
      { id: "totalAmount", header: "Amount" },
      { id: "amountPaid", header: "Amount Paid" },
      { id: "paymentMethod", header: "Payment" },
      { id: "paymentStatus", header: "Paid" },
      { id: "guestNumber", header: "Guests" },
      { id: "shortId", header: "Reg ID" },
    ],
    [],
  );

  const rowSelection = useMemo(
    () => ({
      selectedIds: selectedRows,
      onToggleRow: (id: string, selected: boolean) => {
        setSelectedRows((prev) => {
          const next = new Set(prev);
          if (selected) {
            next.add(id);
          }
          else {
            next.delete(id);
          }
          return next;
        });
      },
      onTogglePage: (pageRowIds: string[], selectAll: boolean) => {
        setSelectedRows((prev) => {
          const next = new Set(prev);
          if (selectAll) {
            pageRowIds.forEach((id) => next.add(id));
          } else {
            pageRowIds.forEach((id) => next.delete(id));
          }
          return next;
        });
      },
      getRowId: (row: any) => String(row?.id ?? ""),
      isRowSelectable: (row: any) =>
        isMemberFirmRecipient(row?.firmId) &&
        isReminderEligiblePaymentStatus(row?.paymentStatusKey),
    }),
    [selectedRows],
  );


  const formatData = (dataToFormat: any[]) => {
    if (!dataToFormat || !Array.isArray(dataToFormat)) {
      return [];
    }
    return dataToFormat.map((d: any) => {
      const firstName = d.personName || "";
      const lastName = d.personLastName || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      const firmLabel = d.firmId
        ? firmData.find((firm) => String(firm.id) === String(d.firmId))
        : undefined;
      return {
        ...d,
        firmName: firmLabel ? firmShortLabel(firmLabel) || d.firmName : d.firmName,
        personName: fullName || "-",
        paymentStatusKey: d.paymentStatus,
        status:
          attendeesStatusEnum[d.status as keyof typeof attendeesStatusEnum],
        prepaidStatus: d.prepaid ? "Yes" : "No",
        paymentMethod:
          paymentMethodEnum[d.paymentMethod as keyof typeof paymentMethodEnum],
        paymentStatus: statusEnum[d.paymentStatus as keyof typeof statusEnum],
        registrationDate: formatTimeSpanToString(d.registrationDate),
        eventDate:
          d.eventDate != null && d.eventDate !== ""
            ? formatTimeSpanToString(d.eventDate)
            : "TBD",
        attendeeType:
          attendeesTypeEnum[
          (d.attendeeType === "member" ? "regular" : d.attendeeType) as keyof typeof attendeesTypeEnum
          ] ?? d.attendeeType,
        totalAmount: d.totalAmount ?? 0,
        amountPaid: d.amountPaid ?? 0,
      };
    });
  };

  const isGlobalUnpaidsRoute = !routeEventId;

  const defaultQuickFilterSeed = DEFAULT_UNPAID_QUICK_FILTER_SEED;

  const pinnedInputFilters = useMemo(() => {
    if (!routeEventId) {
      return undefined;
    }
    const title =
      event?.title?.trim() || eventsMap.get(routeEventId)?.trim() || "";
    if (!title) {
      return undefined;
    }
    return {
      Event: [{ id: routeEventId, name: title }],
    };
  }, [routeEventId, event?.title, eventsMap]);

  const yearOptions = useMemo(() => getEventYearFilterOptions(), []);

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Year",
        customTitle: "By Year:",
        fieldName: "year",
        type: "select",
        comparison: "EQUAL",
        objectData: yearOptions,
        sortByName: false,
        hidden: !!routeEventId,
      },
      {
        name: "Firm",
        fieldName: "firmId",
        objectData:
          firmData?.map((f: any) => ({
            id: f.id,
            name: firmShortLabel(f),
          })) || [],
        comparison: "EQUAL",
        hidden: !!routeEventId,
      },
      {
        name: "Jurisdiction",
        fieldName: "firmJurisdiction",
        objectData:
          jurisdictionData?.map((c: any) => ({ id: c.id, name: c.name })) || [],
        comparison: "BY_NAME",
      },
      {
        name: "Region",
        fieldName: "firmRegion",
        objectData:
          regionData
            ?.map((r: any) => ({ id: r.id, name: r.name }))
            .filter((r: { id: string; name: string }) => {
              if (!isGlobalUnpaidsRoute) {
                return true;
              }
              const key = (r.name ?? "").trim().toLowerCase();
              return !UNPAIDS_PAGE_EXCLUDED_REGION_NAMES.has(key);
            }) || [],
        comparison: "BY_NAME_CONTAINS",
      },
      {
        name: "Industry",
        fieldName: "industry",
        objectData:
          practiceIndustryData?.map((i: any) => ({
            id: i.name,
            name: i.name,
          })) || [],
        comparison: "IN",
      },
      {
        name: "Paid",
        fieldName: "paymentStatus",
        type: "multiselect",
        objectData: paidStatusOptions,
        comparison: "EQUAL",
        disabledWrite: true,
      },
      {
        name: "Prepaid Registration Status",
        fieldName: "prepaid",
        objectData: [
          { id: "yes", name: "Yes" },
          { id: "no", name: "No" },
        ],
        comparison: "BOOLEAN",
        disabledWrite: true,
      },
      {
        name: "Registration Type",
        fieldName: "attendeeType",
        objectData: Object.entries(attendeesTypeEnumWithoutGuest).map(
          ([id, name]) => ({
            id,
            name,
          }),
        ),
        comparison: "EQUAL",
      },
      {
        name: "Event",
        fieldName: "eventId",
        objectData: Array.from(eventsMap.entries()).map(
          ([id, title]: [string, string]) => ({
            id: id,
            name: title,
          }),
        ),
        comparison: "EQUAL",
      },
      {
        name: "Reg date",
        fieldName: "registrationDate",
        type: "custom",
        defaultCustomFilterValue: DEFAULT_TEMPLATE_DATE_FILTER,
        getCustomSelectedChips: templateDateFlightFilterChips,
        render: ({ value, apply }) => (
          <TemplateDateFlightsPicker
            value={
              (value as TemplateDateFilterValue | null | undefined) ??
              DEFAULT_TEMPLATE_DATE_FILTER
            }
            onApply={(next) => {
              const built = buildRegistrationDateFilter(
                next.templateDatePreset,
                next.templateDateFrom,
                next.templateDateTo,
              );
              apply(next, built?.registrationDate ?? null);
            }}
            variant="catalog"
            label="By reg date:"
            dialogTitle="Registration date"
            labelClassName="font-semibold text-gray-font"
            triggerClassName="rounded-md border border-solid !border-gray-300 !bg-gray-25 !shadow-none !hover:shadow-md"
            triggerInsetClassName="max-h-[0.875rem]"
          />
        ),
      },
      {
        name: "Event date",
        fieldName: "eventDate",
        type: "custom",
        defaultCustomFilterValue: DEFAULT_TEMPLATE_DATE_FILTER,
        getCustomSelectedChips: templateDateFlightFilterChips,
        render: ({ value, apply }) => (
          <TemplateDateFlightsPicker
            value={
              (value as TemplateDateFilterValue | null | undefined) ??
              DEFAULT_TEMPLATE_DATE_FILTER
            }
            onApply={(next) => {
              const built = buildEventDateFilter(
                next.templateDatePreset,
                next.templateDateFrom,
                next.templateDateTo,
              );
              apply(next, built?.eventDate ?? null);
            }}
            variant="catalog"
            label="By event date:"
            dialogTitle="Event date"
            labelClassName="font-semibold text-gray-font"
            triggerClassName="rounded-md border border-solid !border-gray-300 !bg-gray-25 !shadow-none !hover:shadow-md"
            triggerInsetClassName="max-h-[0.875rem]"
          />
        ),
      },
    ],
    [
      yearOptions,
      firmData,
      jurisdictionData,
      regionData,
      practiceIndustryData,
      eventsMap,
      routeEventId,
      isGlobalUnpaidsRoute,
    ],
  );

  const handleSendReminderClick = (): void => {
    if (isSendingEmails) {
      return;
    }

    if (selectedRows.size === 0) {
      toastError("Select at least one unpaid or partial registration.");
      return;
    }

    setIsReminderConfirmOpen(true);
  };

  const handleReminderSendConfirmation = (confirm: boolean): void => {
    if (!confirm) {
      setIsReminderConfirmOpen(false);
      return;
    }

    setIsReminderConfirmOpen(false);
    void handleSendReminderInvoices();
  };

  const reminderConfirmMessage = useMemo(() => {
    const count = selectedRows.size;
    const emailLabel = count === 1 ? "email" : "emails";
    return `Send reminder invoice to selected recipients? ${count} ${emailLabel} will be sent.`;
  }, [selectedRows.size]);

  const handleSendReminderInvoices = async (): Promise<void> => {
    if (isSendingEmails) {
      return;
    }

    const registrationIds = Array.from(selectedRows).filter(Boolean);
    if (registrationIds.length === 0) {
      return;
    }

    setIsSendingEmails(true);
    setIsReminderModalOpen(true);
    setIsReminderComplete(false);
    setEmailProgress({
      completed: 0,
      total: registrationIds.length,
    });
    setReminderResults({
      sentCount: 0,
      skippedCount: 0,
      failedCount: 0,
    });
    setReminderIssues([]);

    const triggeredAt = new Date().toISOString();
    const triggeredById = loggedPerson?.id?.trim() || "";
    const triggeredByName =
      [loggedPerson?.name, loggedPerson?.lastName].filter(Boolean).join(" ") ||
      "Staff";

    const eventCache = new Map<string, IEvent>();
    const firmCache = new Map<string, IFirm>();

    if (routeEventId && event?.id) {
      eventCache.set(event.id, event);
    }

    let sentCount = 0;
    let skippedCount = 0;
    let failedCount = 0;
    const issues: PaymentReminderIssue[] = [];
    const recipients: IPaymentReminderLogRecipient[] = [];

    const recordRecipient = (entry: IPaymentReminderLogRecipient) => {
      recipients.push(entry);
    };

    const recordIssue = (
      issue: PaymentReminderIssue,
      registrationId: string,
    ) => {
      issues.push(issue);
      setReminderIssues([...issues]);
      recordRecipient({
        registrationId,
        personName: issue.name,
        outcome: issue.kind,
        reason: issue.reason,
      });
    };

    try {
      for (const registrationId of registrationIds) {
        let displayName = `Registration ${registrationId}`;

        try {
          const registration = await getRegistrationById(registrationId);
          displayName = getRegistrationDisplayName(registration);
          const eventId = registration?.eventId || routeEventId;

          if (!eventId) {
            skippedCount++;
            recordIssue(
              {
                name: displayName,
                kind: "skipped",
                reason: "Event could not be found for this registration.",
              },
              registrationId,
            );
            continue;
          }

          if (!isReminderEligiblePaymentStatus(registration?.paymentStatus)) {
            skippedCount++;
            recordIssue(
              {
                name: displayName,
                kind: "skipped",
                reason: "Registration is not unpaid or partial.",
              },
              registrationId,
            );
            continue;
          }

          if (!isMemberFirmRecipient(registration?.firmId)) {
            skippedCount++;
            recordIssue(
              {
                name: displayName,
                kind: "skipped",
                reason: "Reminder invoices are only sent for member firm registrations.",
              },
              registrationId,
            );
            continue;
          }

          let registrationEvent = eventCache.get(eventId);
          if (!registrationEvent) {
            const loadedEvent = await getEventById(eventId);
            if (!loadedEvent) {
              skippedCount++;
              recordIssue(
                {
                  name: displayName,
                  kind: "skipped",
                  reason: "Event details could not be loaded.",
                },
                registrationId,
              );
              continue;
            }
            registrationEvent = loadedEvent;
            eventCache.set(eventId, loadedEvent);
          }

          let firm: IFirm | null = null;
          const firmId = registration.firmId;
          if (firmId) {
            firm = firmCache.get(firmId) ?? null;
            if (!firm) {
              try {
                firm = await getFirmById(firmId);
                firmCache.set(firmId, firm);
              } catch {
                firm = null;
              }
            }
          }

          const result = await sendDuePaymentReminderForRegistration(
            registration,
            registrationEvent,
            firm,
          );

          if (result.outcome === "sent") {
            sentCount++;
            recordRecipient({
              registrationId,
              personName: displayName,
              outcome: "sent",
            });
          } else if (result.outcome === "skipped") {
            skippedCount++;
            recordIssue(
              {
                name: displayName,
                kind: "skipped",
                reason: result.reason || "Registration was skipped.",
              },
              registrationId,
            );
          } else {
            failedCount++;
            recordIssue(
              {
                name: displayName,
                kind: "failed",
                reason: result.reason || "Could not send the reminder invoice.",
              },
              registrationId,
            );
          }
        } catch (error) {
          console.error(`Error sending reminder invoice for ${registrationId}:`, error);
          failedCount++;
          const message =
            error instanceof Error
              ? error.message
              : "An unexpected error occurred.";
          recordIssue(
            {
              name: displayName,
              kind: "failed",
              reason: message,
            },
            registrationId,
          );
        } finally {
          setEmailProgress((prev) => ({
            ...prev,
            completed: prev.completed + 1,
          }));
          setReminderResults({
            sentCount,
            skippedCount,
            failedCount,
          });
        }
      }
    } finally {
      setIsSendingEmails(false);
      setIsReminderComplete(true);
      setReminderResults({
        sentCount,
        skippedCount,
        failedCount,
      });
      setReminderIssues([...issues]);

      try {
        await savePaymentReminderBatchLog({
          eventId: routeEventId || null,
          eventName: routeEventId ? event?.title || null : null,
          triggeredAt,
          completedAt: new Date().toISOString(),
          triggeredById,
          triggeredByName,
          totalSelected: registrationIds.length,
          sentCount,
          failedCount,
          skippedCount,
          selectedRegistrationIds: registrationIds,
          recipients,
        });
        setReminderLogRefreshKey((prev) => prev + 1);
      } catch (logError) {
        console.error("Failed to save payment reminder batch log:", logError);
      }
    }
  };

  const handleCloseReminderModal = () => {
    setIsReminderModalOpen(false);
    setIsReminderComplete(false);
    setEmailProgress({ completed: 0, total: 0 });
    setReminderResults({ sentCount: 0, skippedCount: 0, failedCount: 0 });
    setReminderIssues([]);
  };

  useEffect(() => {
    if (!routeEventId) {
      clearPageMeta();
      return;
    }

    setPageMeta({
      breadcrumbLabels: {
        [`/events-staff/${routeEventId}`]: event?.title || "Loading",
      },
    });
  }, [clearPageMeta, event?.title, routeEventId, setPageMeta]);

  const quickFilters: IQuickFilter[] = [
    REGISTRATION_ALL_ATTENDEES_QUICK_FILTER,
    REGISTRATION_WSG_STAFF_QUICK_FILTER,
  ];

  return (
    <>
      <ModalConfirmation
        openModal={isReminderConfirmOpen}
        setOpenModal={setIsReminderConfirmOpen}
        setConfirmation={handleReminderSendConfirmation}
        title="Send Reminder Invoice"
        message={reminderConfirmMessage}
      />
      <PaymentReminderBatchLogModal
        isOpen={isReminderLogModalOpen}
        onClose={() => setIsReminderLogModalOpen(false)}
        eventId={routeEventId}
        refreshKey={reminderLogRefreshKey}
      />
      <PaymentReminderProgressModal
        isOpen={isReminderModalOpen}
        completed={emailProgress.completed}
        total={emailProgress.total}
        sentCount={reminderResults.sentCount}
        failedCount={reminderResults.failedCount}
        skippedCount={reminderResults.skippedCount}
        issues={reminderIssues}
        isComplete={isReminderComplete}
        onClose={handleCloseReminderModal}
      />
      <FilteredTableCore
        columnsDef={columnsDef}
        showRowNumber
        rowSelection={rowSelection}
        endpoint={endpoint}
        filtersData={filtersData}
        formatData={formatData}
        quickFilters={quickFilters}
        persistentFilter={persistentFilter}
        mergePanelFiltersIntoPersistentFilter
        pinnedInputFilters={pinnedInputFilters}
        persistFilters={Boolean(routeEventId)}
        defaultQuickFilterSeed={defaultQuickFilterSeed}
        defaultSorting={{ field: "registrationDate", order: "desc" }}
        quickFilterRequireSelection
        defaultPageSize={attendeesTableDefaultPageSize}
        quickFilterBarAccessory={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              text="View Logs"
              color="secondary"
              variant="filled"
              size="fixed"
              onClick={() => setIsReminderLogModalOpen(true)}
              icon={<Log />}
            />
          </div>
        }
        searchToolbarAccessory={<Button
          text="Send Reminder Invoice"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={handleSendReminderClick}
          disabled={isSendingEmails || selectedRows.size === 0}
          icon={<SendMail />}
        />}
        showSearchInput
        searchPlaceholder="Search by Name, Event, Reg Type, Firm"
      />
    </>
  );
}
